import { faker } from '@faker-js/faker';
import {
  SAMPLE_AVATARS,
  SAMPLE_GEMS,
  SAMPLE_COVERS,
  avatarAt,
  gemAt,
  coverAt,
} from '../constants/sampleImages';

const SL_LOCATIONS = ['Ratnapura', 'Colombo', 'Beruwala', 'Elahera', 'Galle', 'Kandy', 'Matale'];
const GEM_TYPES = ['Sapphire', 'Ruby', 'Emerald', 'Spinel', 'Alexandrite', 'Cat\'s Eye', 'Moonstone', 'Amethyst', 'Topaz'];
const GEM_VARIETIES = ['Ceylon Blue', 'Yellow', 'Padparadscha', 'Star', 'Pink', 'White'];
const CUTS = ['Excellent', 'Very Good', 'Good', 'Fair'];
const CLARITY = ['IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1'];
const TREATMENTS = ['None (Unheated)', 'Heat Treated', 'Beryllium Treated', 'Diffusion'];
const ORIGINS = ['Sri Lanka', 'Ratnapura', 'Beruwala', 'Colombo', 'Madagascar'];

export const generateUsers = (count) => {
  return Array.from({ length: count }, (_, i) => ({
    uid: faker.string.uuid(),
    displayName: faker.person.fullName(),
    email: faker.internet.email(),
    photoURL: avatarAt(i),
    role: faker.helpers.arrayElement(['USER', 'VENDOR']),
    bio: faker.lorem.sentence(),
    followersCount: faker.number.int({ min: 0, max: 1000 }),
    followingCount: faker.number.int({ min: 0, max: 500 }),
    createdAt: faker.date.past({ years: 2 }).toISOString(),
    lastActive: faker.date.recent().toISOString(),
  }));
};

export const generateVendors = (users) => {
  let vendorUsers = users.filter(u => u.role === 'VENDOR');

  if (vendorUsers.length === 0 && users.length > 0) {
    users[0].role = 'VENDOR';
    vendorUsers = [users[0]];
  }

  return vendorUsers.map((user, i) => ({
    id: faker.string.uuid(),
    userId: user.uid,
    companyName: faker.company.name() + ' Gems',
    logo: avatarAt(i + 3),
    coverImage: coverAt(i),
    description: faker.company.catchPhrase(),
    location: faker.helpers.arrayElement(SL_LOCATIONS),
    rating: faker.number.float({ min: 3.5, max: 5, multipleOf: 0.1 }),
    reviewCount: faker.number.int({ min: 0, max: 500 }),
    followersCount: faker.number.int({ min: 0, max: 5000 }),
    verified: faker.datatype.boolean(0.7),
    verificationLevel: faker.helpers.arrayElement(['BLUE', 'GREEN', 'GOLD', 'NIC']),
    contactEmail: faker.internet.email(),
    phone: faker.phone.number(),
    website: faker.internet.url(),
    createdAt: user.createdAt,
  }));
};

export const generateListings = (vendors, count) => {
  return Array.from({ length: count }, (_, i) => {
    const vendor = faker.helpers.arrayElement(vendors);
    const gemType = faker.helpers.arrayElement(GEM_TYPES);
    const title = `${faker.helpers.arrayElement(GEM_VARIETIES)} ${gemType}`;
    const isSold = faker.datatype.boolean({ probability: 0.2 });
    const primaryGem = gemAt(i);
    const secondaryGem = gemAt(i + 3);
    return {
      id: faker.string.uuid(),
      vendorId: vendor.id,
      title,
      gemType,
      variety: faker.helpers.arrayElement(GEM_VARIETIES),
      type: gemType,
      caratWeight: faker.number.float({ min: 0.5, max: 20, multipleOf: 0.1 }),
      weight: faker.number.float({ min: 0.5, max: 20, multipleOf: 0.1 }),
      cut: faker.helpers.arrayElement(CUTS),
      clarity: faker.helpers.arrayElement(CLARITY),
      treatment: faker.helpers.arrayElement(TREATMENTS),
      certification: faker.helpers.arrayElement(['GIA', 'GRS', 'None']),
      description: faker.lorem.paragraph(),
      price: faker.number.int({ min: 500, max: 50000 }),
      currency: 'USD',
      origin: faker.helpers.arrayElement(ORIGINS),
      featured: faker.datatype.boolean(0.3),
      images: [primaryGem, secondaryGem],
      status: isSold ? 'SOLD' : 'ACTIVE',
      createdAt: faker.date.recent({ days: 30 }).toISOString(),
    };
  });
};

export const generatePosts = (users, count) => {
  return Array.from({ length: count }, (_, i) => {
    const user = faker.helpers.arrayElement(users);
    return {
      id: faker.string.uuid(),
      authorId: user.uid,
      authorName: user.displayName,
      authorAvatar: user.photoURL || avatarAt(i),
      authorType: user.role === 'VENDOR' ? 'VENDOR' : 'USER',
      content: faker.lorem.paragraph(),
      media: [gemAt(i), gemAt(i + 2)],
      likesCount: faker.number.int({ min: 0, max: 500 }),
      likes: Array.from({ length: 5 }, () => faker.string.uuid()),
      ratingsCount: faker.number.int({ min: 10, max: 200 }),
      averageRating: faker.number.float({ min: 4, max: 5, multipleOf: 0.1 }),
      commentsCount: faker.number.int({ min: 0, max: 100 }),
      sharesCount: faker.number.int({ min: 0, max: 50 }),
      type: faker.helpers.arrayElement(['DISCUSSION', 'STORY', 'NEWS']),
      createdAt: faker.date.recent({ days: 14 }).toISOString(),
    };
  });
};

// Re-export for convenience
export { SAMPLE_AVATARS, SAMPLE_GEMS, SAMPLE_COVERS };
